import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import { showToast } from '@/utils/toast';
import {
  useReferralReferees,
  useMyReferrer,
  usePendingRewards,
  useClaimRewards,
} from '@hooks';
import { Loading } from '@/components/common';
import type { Friend, UserProfile, PendingReward } from '@types';
import {
  generateAvatarFromAddress,
  generateNameFromAddress,
} from '@/shared/utils';
import goldIcon from '@/assets/header/gold.webp';
import pointIcon from '@/assets/header/point.webp';
import HeroCry from '@/assets/logout-modal/hero-cry.webp';

// ============ Confetti Celebration Component ============
interface ConfettiCelebrationProps {
  show: boolean;
  goldAmount: number;
  onComplete: () => void;
}

const ConfettiCelebration = ({
  show,
  goldAmount,
  onComplete,
}: ConfettiCelebrationProps) => {
  const [windowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={200}
          gravity={0.3}
          colors={['#FFD700', '#FFA500', '#FF6B00', '#FFEC8B', '#FFE135']}
          onConfettiComplete={onComplete}
        />

        {/* Gold Claimed Message */}
        <motion.div
          className="absolute flex flex-col items-center gap-4"
          initial={{ scale: 0, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0, y: -50 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          <motion.img
            src={goldIcon}
            alt="Gold"
            className="w-24 h-24"
            animate={{
              rotate: [0, -10, 10, -10, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 0.6,
              repeat: 2,
            }}
            style={{ filter: 'drop-shadow(0 0 20px rgba(255, 215, 0, 0.8))' }}
          />
          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <h2
              className="text-4xl font-bold text-white mb-2"
              style={{
                fontFamily: 'Lilita One',
                textShadow:
                  '2px 2px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000',
              }}
            >
              +{goldAmount} Gold!
            </h2>
            <p
              className="text-xl text-yellow-200"
              style={{ textShadow: '1px 1px 0 #000' }}
            >
              Rewards Claimed!
            </p>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ============ Claim Button Component ============
interface ClaimButtonProps {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  goldAmount: number;
  size?: 'sm' | 'md';
}

const ClaimButton = ({
  onClick,
  disabled,
  loading,
  goldAmount,
  size = 'md',
}: ClaimButtonProps) => {
  const isSmall = size === 'sm';
  const buttonHeight = isSmall ? '40px' : '52px';
  const buttonPadding = isSmall ? '8px 16px' : '10px 20px';

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled || loading}
      className="relative flex items-center justify-center gap-2 isolate"
      style={{
        boxSizing: 'border-box',
        padding: buttonPadding,
        height: buttonHeight,
        background: disabled
          ? 'linear-gradient(135deg, #666 0%, #555 49.04%, #444 100%)'
          : 'linear-gradient(135deg, #FF7A00 0%, #FFA500 49.04%, #FFD700 100%)',
        border: '2px solid #000000',
        borderRadius: '12px',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      whileHover={disabled ? {} : { scale: 1.05 }}
      whileTap={disabled ? {} : { scale: 0.95 }}
    >
      {/* Highlight effect - same as CopyButton parent */}
      {!disabled && (
        <div
          className="absolute"
          style={{
            width: 'calc(100% - 16px)',
            left: '8px',
            top: '8.33%',
            bottom: '50%',
            background: 'rgba(255, 255, 255, 0.35)',
            borderRadius: '6px',
            zIndex: 0,
          }}
        />
      )}

      {loading ? (
        <motion.div
          className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
          style={{ zIndex: 1 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      ) : (
        <>
          <img
            src={goldIcon}
            alt="Gold"
            style={{
              width: isSmall ? '20px' : '28px',
              height: isSmall ? '20px' : '28px',
              filter: 'drop-shadow(1px 2px 0px #000000)',
              zIndex: 1,
            }}
          />
          <span
            style={{
              fontFamily: 'Lilita One',
              fontSize: isSmall ? '14px' : '18px',
              color: '#FFFFFF',
              filter: 'drop-shadow(1px 2px 0px #000000)',
              zIndex: 1,
            }}
          >
            Claim +{goldAmount}
          </span>
        </>
      )}
    </motion.button>
  );
};

// ============ Referrer Card Component ============
interface ReferrerCardProps {
  referrerWallet: string;
  signupBonus: number;
  hasPendingReward: boolean;
  onClaim: () => void;
  isClaiming: boolean;
}

const ReferrerCard = ({
  referrerWallet,
  signupBonus,
  hasPendingReward,
  onClaim,
  isClaiming,
}: ReferrerCardProps) => {
  return (
    <motion.div
      className="w-full"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <div
        className="relative p-4 backdrop-blur-md overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, rgba(138, 43, 226, 0.4) 0%, rgba(75, 0, 130, 0.4) 100%)',
          border: '2px solid rgba(186, 85, 211, 0.6)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(138, 43, 226, 0.3)',
        }}
      >
        {/* Glow effect */}
        <div
          className="absolute -top-20 -right-20 w-40 h-40 rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(186, 85, 211, 0.4) 0%, transparent 70%)',
            filter: 'blur(20px)',
          }}
        />

        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-2 h-2 rounded-full"
            style={{ background: '#BA55D3' }}
          />
          <span
            style={{
              fontFamily: 'Lilita One',
              fontSize: '14px',
              color: '#E6E6FA',
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}
          >
            Your Referrer
          </span>
        </div>

        {/* Content */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src={generateAvatarFromAddress(referrerWallet, 48)}
              alt="Referrer"
              className="w-12 h-12 rounded-xl border-2"
              style={{ borderColor: 'rgba(186, 85, 211, 0.6)' }}
            />
            <div>
              <p
                style={{
                  fontFamily: 'Lilita One',
                  fontSize: '16px',
                  color: '#FFF',
                  textShadow: '1px 1px 0 #000',
                }}
              >
                {generateNameFromAddress(referrerWallet)}
              </p>
              <p
                style={{
                  fontSize: '12px',
                  color: '#D8BFD8',
                }}
              >
                Invited you to play!
              </p>
            </div>
          </div>

          {hasPendingReward ? (
            <ClaimButton
              onClick={onClaim}
              goldAmount={signupBonus}
              loading={isClaiming}
              size="sm"
            />
          ) : (
            <div
              className="px-4 py-2 rounded-lg"
              style={{
                background: 'rgba(0, 128, 0, 0.3)',
                border: '1px solid rgba(0, 255, 0, 0.3)',
              }}
            >
              <span
                style={{
                  fontFamily: 'Lilita One',
                  fontSize: '14px',
                  color: '#90EE90',
                }}
              >
                Claimed
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ============ Pending Rewards Section ============
interface PendingRewardsSectionProps {
  pendingRewards: PendingReward[];
  totalGold: number;
  onClaimAll: () => void;
  isClaiming: boolean;
}

const PendingRewardsSection = ({
  pendingRewards,
  totalGold,
  onClaimAll,
  isClaiming,
}: PendingRewardsSectionProps) => {
  if (pendingRewards.length === 0) return null;

  return (
    <motion.div
      className="w-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div
        className="relative p-4 backdrop-blur-md overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, rgba(255, 165, 0, 0.3) 0%, rgba(255, 140, 0, 0.2) 100%)',
          border: '2px solid rgba(255, 215, 0, 0.5)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(255, 165, 0, 0.2)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <motion.img
              src={goldIcon}
              alt="Gold"
              className="w-8 h-8"
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <div>
              <span
                style={{
                  fontFamily: 'Lilita One',
                  fontSize: '18px',
                  color: '#FFF',
                  textShadow: '1px 1px 0 #000',
                }}
              >
                Pending Rewards
              </span>
              <p
                style={{
                  fontSize: '12px',
                  color: '#FFE4B5',
                }}
              >
                {pendingRewards.length} reward
                {pendingRewards.length > 1 ? 's' : ''} waiting!
              </p>
            </div>
          </div>

          <ClaimButton
            onClick={onClaimAll}
            goldAmount={totalGold}
            loading={isClaiming}
            size="md"
          />
        </div>

        {/* Reward list */}
        <div className="flex flex-col gap-2">
          {pendingRewards.map((reward, index) => (
            <motion.div
              key={reward.id}
              className="flex items-center justify-between p-3 rounded-lg"
              style={{
                background: 'rgba(0, 0, 0, 0.3)',
              }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index }}
            >
              <div className="flex items-center gap-3">
                <img
                  src={generateAvatarFromAddress(reward.relatedUserWallet, 32)}
                  alt="User"
                  className="w-8 h-8 rounded-lg border border-white/20"
                />
                <div>
                  <p
                    style={{
                      fontFamily: 'Lilita One',
                      fontSize: '14px',
                      color: '#FFF',
                    }}
                  >
                    {generateNameFromAddress(reward.relatedUserWallet)}
                  </p>
                  <p
                    style={{
                      fontSize: '11px',
                      color: '#FFE4B5',
                    }}
                  >
                    {reward.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <img src={goldIcon} alt="Gold" className="w-5 h-5" />
                <span
                  style={{
                    fontFamily: 'Lilita One',
                    fontSize: '16px',
                    color: '#FFD700',
                    textShadow: '1px 1px 0 #000',
                  }}
                >
                  +{reward.goldAmount}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// ============ Friend List Item ============
interface FriendListItemProps {
  friend: Friend;
}

const FriendListItem = ({ friend }: FriendListItemProps) => {
  return (
    <motion.div
      className="relative w-full"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div
        className="relative flex items-center gap-1 isolate backdrop-blur-sm"
        style={{
          padding: '12px 16px',
          background: 'rgba(139, 139, 139, 0.5)',
          border: '1px solid #000000',
          borderRadius: '12px',
          boxSizing: 'border-box',
        }}
      >
        <div
          className="absolute"
          style={{
            height: '28px',
            left: '4px',
            right: '4px',
            top: '4px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            zIndex: 0,
          }}
        />

        <div
          className="relative flex-shrink-0"
          style={{
            width: '40px',
            height: '40px',
            zIndex: 1,
          }}
        >
          <img
            src={generateAvatarFromAddress(friend.refereeWallet, 40)}
            alt={generateNameFromAddress(friend.refereeWallet)}
            className="absolute inset-0 w-full h-full object-cover rounded-lg border-2 border-[#1e3a8a70]"
          />
        </div>

        <span
          className="flex-1 truncate ml-2"
          style={{
            width: '186px',
            height: '18px',
            fontFamily: 'Lilita One',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '18px',
            color: '#FFFFFF',
            zIndex: 2,
          }}
        >
          {generateNameFromAddress(friend.refereeWallet)}
        </span>

        <div
          className="relative flex items-center isolate flex-shrink-0"
          style={{
            height: '32px',
            zIndex: 3,
          }}
        >
          <img
            src={pointIcon}
            alt="Point"
            style={{
              width: '32px',
              height: '32px',
              filter: 'drop-shadow(1px 1px 0px #000000)',
              margin: '0px -13px',
              zIndex: 1,
            }}
          />

          <div
            className="flex items-center"
            style={{
              padding: '6px 8px 6px 25px',
              gap: '4px',
              height: '22px',
              background: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '4px',
              zIndex: 0,
            }}
          >
            <span
              style={{
                fontFamily: 'Lilita One',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '16px',
                color: '#FFFFFF',
                textShadow: '0.5px 0.5px 0px #000000',
              }}
            >
              {friend.refereeScore}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============ Referral Link Component ============
interface ReferralLinkProps {
  referralLink: string;
}

const ReferralLink = ({ referralLink }: ReferralLinkProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      showToast.success('Copied to clipboard!', {
        style: {
          background: '#4CAF50',
          color: '#fff',
        },
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      showToast.error('Failed to copy link', {
        style: {
          background: '#f44336',
          color: '#fff',
        },
      });
    }
  };

  return (
    <motion.div
      className="flex items-center gap-2 w-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
    >
      <div
        className="relative flex items-center isolate flex-grow backdrop-blur-sm"
        style={{
          boxSizing: 'border-box',
          padding: '4px 12px',
          gap: '4px',
          width: '277px',
          height: '52px',
          background: 'rgba(0, 0, 0, 0.5)',
          border: '1px solid #000000',
          boxShadow: '1px 2px 0px #1A003B',
          borderRadius: '12px',
        }}
      >
        <div
          className="absolute"
          style={{
            height: '28px',
            left: '4px',
            right: '4px',
            top: '4px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            zIndex: 0,
          }}
        />

        <span
          className="truncate"
          style={{
            height: '21px',
            fontFamily: 'Lilita One',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '18px',
            lineHeight: '21px',
            color: '#FFFFFF',
            zIndex: 1,
          }}
        >
          {referralLink}
        </span>
      </div>

      <motion.button
        onClick={handleCopy}
        className="relative flex items-center justify-center isolate flex-shrink-0"
        style={{
          boxSizing: 'border-box',
          padding: '10px',
          gap: '4px',
          width: '52px',
          height: '52px',
          background:
            'linear-gradient(135deg, #FF7A00 0%, #FFA500 49.04%, #FFD700 100%)',
          border: '2px solid #000000',
          borderRadius: '12px',
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <div
          className="absolute"
          style={{
            width: '40px',
            left: 'calc(50% - 40px/2)',
            top: '8.33%',
            bottom: '45.83%',
            background: 'rgba(255, 255, 255, 0.35)',
            borderRadius: '6px',
            zIndex: 0,
          }}
        />

        {copied ? (
          <svg
            className="text-white"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            viewBox="0 0 24 24"
            style={{
              width: '32px',
              height: '32px',
              filter: 'drop-shadow(1px 2px 0px #000000)',
              zIndex: 1,
            }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : (
          <CopyButton />
        )}
      </motion.button>
    </motion.div>
  );
};

// ============ Main FrensContent Component ============
interface FrensContentProps {
  userProfile: UserProfile;
}

export const FrensContent = ({ userProfile }: FrensContentProps) => {
  const {
    referees,
    isLoading: isLoadingReferees,
    error,
  } = useReferralReferees();
  const { referrer, isLoading: isLoadingReferrer } = useMyReferrer();
  const {
    pendingRewards,
    totalPendingGold,
    hasPendingRewards,
    isLoading: isLoadingPending,
  } = usePendingRewards();
  const { claimAll, isClaiming } = useClaimRewards();

  const [showConfetti, setShowConfetti] = useState(false);
  const [claimedGold, setClaimedGold] = useState(0);

  const referralLink = `${window.location.origin}?ref=${userProfile.id}`;
  const totalFriends = referees.length;

  const handleClaimAll = useCallback(async () => {
    try {
      const result = await claimAll();
      if (result.success && result.totalGold > 0) {
        setClaimedGold(result.totalGold);
        setShowConfetti(true);
        showToast.success(result.message, {
          style: {
            background: '#4CAF50',
            color: '#fff',
          },
        });
      } else if (result.claimedCount === 0) {
        showToast.custom('No rewards to claim', {
          style: {
            background: '#2196F3',
            color: '#fff',
          },
        });
      }
    } catch (err) {
      console.error('Failed to claim rewards:', err);
      showToast.error('Failed to claim rewards', {
        style: {
          background: '#f44336',
          color: '#fff',
        },
      });
    }
  }, [claimAll]);

  const handleConfettiComplete = useCallback(() => {
    setShowConfetti(false);
    setClaimedGold(0);
  }, []);

  const isLoading = isLoadingReferees || isLoadingReferrer || isLoadingPending;

  // Show pending rewards section if:
  // 1. User has pending rewards from being a referrer (friends signed up)
  // 2. AND user doesn't have a referrer with pending reward (to avoid duplicate display)
  const showPendingRewardsSection = hasPendingRewards && !referrer?.hasPendingReward;

  return (
    <div className="w-full h-full relative overflow-hidden flex flex-col">
      {/* Confetti Celebration */}
      <ConfettiCelebration
        show={showConfetti}
        goldAmount={claimedGold}
        onComplete={handleConfettiComplete}
      />

      {/* Scrollable Content */}
      <div className="fixed top-[130px] bottom-[100px] left-0 right-0 max-w-[var(--app-max-width)] mx-auto px-3 overflow-y-auto z-30">
        {/* Header */}
        <motion.div
          className="flex flex-col items-center p-0 w-full gap-2 mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1
            className="w-full font-normal text-[38px] leading-[43px] text-center text-white"
            style={{
              WebkitTextStroke: '1px #000',
              textShadow: '1px 1px 0px #000',
            }}
          >
            Friends list
          </h1>

          <div
            className="flex flex-col items-center p-0 gap-1"
            style={{
              textShadow: '0.5px 1px 0px #000',
            }}
          >
            <p className="font-normal text-sm leading-4 text-white">
              Invite your buddies and score rewards!
            </p>
            <p className="font-normal text-sm leading-4 text-white">
              Referrer gets +2 Gold, invited friend grabs +1 Gold
            </p>
          </div>
        </motion.div>

        {/* Referrer Section */}
        {referrer && (
          <div className="mb-4">
            <ReferrerCard
              referrerWallet={referrer.referrerWallet}
              signupBonus={referrer.signupBonus}
              hasPendingReward={referrer.hasPendingReward}
              onClaim={handleClaimAll}
              isClaiming={isClaiming}
            />
          </div>
        )}

        {/* Pending Rewards Section */}
        {showPendingRewardsSection && (
          <div className="mb-4">
            <PendingRewardsSection
              pendingRewards={pendingRewards}
              totalGold={totalPendingGold}
              onClaimAll={handleClaimAll}
              isClaiming={isClaiming}
            />
          </div>
        )}

        {/* Total Friends Header */}
        <div className="w-full text-left mb-3">
          <span
            className="font-bold text-2xl text-white"
            style={{
              WebkitTextStroke: '0.1px #000',
              textShadow: '1px 0px 0px #000',
            }}
          >
            Total friends: {totalFriends.toString().padStart(2, '0')}
          </span>
        </div>

        {/* Friend List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loading text="Loading friends" size="md" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-red-400 text-lg">Failed to load friends</p>
            </div>
          ) : referees.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 relative">
              <div className="relative z-10 flex flex-col items-center">
                <motion.img
                  src={HeroCry}
                  alt="Character"
                  className="size-[180px] object-contain"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 200,
                  }}
                />
                <p
                  className="text-white text-lg text-center"
                  style={{
                    WebkitTextStroke: '0.1px #000',
                    textShadow: '1px 0px 0px #000',
                  }}
                >
                  No allies yet, no stories told
                  <br />
                  Invite a friend to earn that Gold!
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2 pb-20">
              {referees.map((friend, index) => (
                <motion.div
                  key={friend.refereeId}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.05 }}
                  className="w-full"
                >
                  <FriendListItem friend={friend} />
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Fixed Referral Link at Bottom */}
      <div className="fixed bottom-[100px] left-0 right-0 max-w-[var(--app-max-width)] mx-auto px-3 z-40">
        <ReferralLink referralLink={referralLink} />
      </div>
    </div>
  );
};

const CopyButton = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g filter="url(#filter0_d_815_2411)">
      <path
        d="M14.6667 26H22.2667C21.8723 26.9855 21.1914 27.83 20.3119 28.4242C19.4323 29.0185 18.3948 29.3352 17.3333 29.3333H8C7.29956 29.3335 6.60595 29.1957 5.9588 28.9277C5.31164 28.6598 4.72362 28.2669 4.22834 27.7717C3.73306 27.2764 3.34021 26.6884 3.07226 26.0412C2.80431 25.394 2.66649 24.7004 2.66669 24V13.3333C2.66827 12.0346 3.14287 10.7808 4.00176 9.80662C4.86065 8.83241 6.04499 8.20441 7.33331 8.04006V18.6667C7.33331 22.7067 10.6266 26 14.6667 26ZM26 8.33331H28.8267C28.7447 8.21315 28.6509 8.10149 28.5466 8L24 3.45312C23.9018 3.34932 23.7896 3.25971 23.6667 3.18687V6C23.6696 6.61793 23.9164 7.20971 24.3533 7.64665C24.7903 8.0836 25.3821 8.33037 26 8.33331ZM26 10.3333C24.8514 10.3311 23.7505 9.87384 22.9383 9.06167C22.1262 8.24949 21.6689 7.14859 21.6667 6V2.66669H14.6667C13.9662 2.66649 13.2726 2.80431 12.6255 3.07226C11.9783 3.34021 11.3903 3.73305 10.895 4.22834C10.3997 4.72362 10.0069 5.31164 9.73891 5.95879C9.47095 6.60595 9.33312 7.29956 9.33331 8V18.6667C9.33313 19.3671 9.47096 20.0607 9.73892 20.7079C10.0069 21.355 10.3997 21.9431 10.895 22.4383C11.3903 22.9336 11.9783 23.3265 12.6255 23.5944C13.2726 23.8624 13.9663 24.0002 14.6667 24H24C24.7004 24.0002 25.394 23.8624 26.0412 23.5944C26.6883 23.3265 27.2764 22.9336 27.7716 22.4383C28.2669 21.943 28.6598 21.355 28.9277 20.7079C29.1957 20.0607 29.3335 19.3671 29.3333 18.6667V10.3333H26Z"
        fill="white"
      />
    </g>
    <defs>
      <filter
        id="filter0_d_815_2411"
        x="0"
        y="0"
        width="33"
        height="34"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dx="1" dy="2" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_815_2411"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_815_2411"
          result="shape"
        />
      </filter>
    </defs>
  </svg>
);
